import * as faceapi from '@vladmandic/face-api';
import {
	FaceDetection,
	FaceLandmarks68,
	WithFaceDescriptor,
	WithFaceLandmarks,
} from '@vladmandic/face-api';

const TARGET_LABEL = 'careReceiver';
const MAX_MATCH_DISTANCE_THRESHOLD = 0.5;

function getFaces(images: faceapi.TNetInput[], faceDescriptorToExclude?: Float32Array[]) {
	let faceMatcher: undefined | faceapi.FaceMatcher;
	if (faceDescriptorToExclude?.length) {
		faceMatcher = new faceapi.FaceMatcher(
			new faceapi.LabeledFaceDescriptors(TARGET_LABEL, faceDescriptorToExclude),
			MAX_MATCH_DISTANCE_THRESHOLD,
		);
	}

	return Promise.all(
		images.map(async (image) => {
			const detectedFaces = await detectFaces(image);

			if (!faceMatcher) {
				return getFacesPositions(detectedFaces);
			}

			const matchedFaces = detectedFaces.map((face) => {
				const bestMatch = (faceMatcher as faceapi.FaceMatcher).findBestMatch(face.descriptor);

				return { ...face, bestMatch };
			});

			const faceToExclude = matchedFaces.reduce<undefined | (typeof matchedFaces)[number]>(
				(topMatch, currentFace) => {
					if (currentFace.bestMatch.label !== TARGET_LABEL) {
						return topMatch;
					}

					if (!topMatch) {
						return currentFace;
					}

					if (currentFace.bestMatch.distance < topMatch.bestMatch.distance) {
						return currentFace;
					}

					return topMatch;
				},
				undefined,
			);

			return getFacesPositions(matchedFaces.filter((face) => face !== faceToExclude));
		}),
	);
}

function detectFaces(image: faceapi.TNetInput) {
	return faceapi
		.detectAllFaces(
			image,
			new faceapi.SsdMobilenetv1Options({
				minConfidence: 0.28,
			}),
		)
		.withFaceLandmarks()
		.withFaceDescriptors();
}

function getFacesPositions(
	faces: WithFaceDescriptor<
		WithFaceLandmarks<
			{
				detection: FaceDetection;
			},
			FaceLandmarks68
		>
	>[],
) {
	return faces.map(
		({
			detection: {
				box: { x, y, width, height },
			},
		}) => {
			return { top: y, left: x, width, height };
		},
	);
}

async function getFaceDescriptorFor(referenceImage: HTMLImageElement) {
	const detection = await faceapi
		.detectSingleFace(referenceImage)
		.withFaceLandmarks()
		.withFaceDescriptor();

	if (!detection) {
		return null;
	}

	return detection.descriptor;
}

export { getFaces, getFaceDescriptorFor };
